import { useCallback, useMemo, useState, type ReactElement } from 'react';
import type { PaperProps } from '@mui/material/Paper';
import QuickCreateMenuPaper from '../feedback/QuickCreateMenuPaper';

export interface UseQuickCreateMenuPaperOptions {
  canCreate: boolean;
  createLabel: string;
  /** Opens the module dialog / route. Hook only closes the menu first (Select). */
  onQuickCreate: () => void;
}

/**
 * Wires `QuickCreateMenuPaper` for Select (`MenuProps.slotProps.paper`) and
 * Autocomplete (`slots.paper`). Does not own dialog or API state.
 */
export const useQuickCreateMenuPaper = ({
  canCreate,
  createLabel,
  onQuickCreate,
}: UseQuickCreateMenuPaperOptions) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const onMenuOpen = useCallback(() => setMenuOpen(true), []);
  const onMenuClose = useCallback(() => setMenuOpen(false), []);

  const selectMenuPaperSlotProps = useMemo(
    () => ({
      component: QuickCreateMenuPaper,
      showCreate: canCreate,
      createLabel,
      onCreate: () => {
        setMenuOpen(false);
        onQuickCreate();
      },
    }),
    [canCreate, createLabel, onQuickCreate]
  );

  const makeAutocompletePaper = useCallback(
    (props: PaperProps): ReactElement => (
      <QuickCreateMenuPaper
        {...props}
        showCreate={canCreate}
        createLabel={createLabel}
        onCreate={onQuickCreate}
      />
    ),
    [canCreate, createLabel, onQuickCreate]
  );

  return {
    menuOpen,
    setMenuOpen,
    onMenuOpen,
    onMenuClose,
    selectMenuPaperSlotProps,
    makeAutocompletePaper,
  };
};
